import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import { ProfileImage } from '../../components/ui/ProfileImage';
import { useAdminTestimonials, useApproveTestimonial, useFeatureTestimonial, useDeleteTestimonial } from '../../hooks/unified/useTestimonialsQueries';
import type { Testimonial } from '../../services/unified/testimonialsModule';

type FilterType = 'all' | 'approved' | 'pending' | 'featured';

export function AdminTestimonialsPage() {
  const [filter, setFilter] = useState<FilterType>('all');
  const { data: testimonials = [], isLoading, error, refetch } = useAdminTestimonials();
  const approveMutation = useApproveTestimonial();
  const featureMutation = useFeatureTestimonial();
  const deleteMutation = useDeleteTestimonial();

  const handleApprove = async (id: string, approved: boolean) => {
    try { await approveMutation.mutateAsync({ id, approved }); } catch (err) { console.error('Failed:', err); }
  };
  const handleFeature = async (id: string, featured: boolean) => {
    try { await featureMutation.mutateAsync({ id, featured }); } catch (err) { console.error('Failed:', err); }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this testimonial?')) return;
    try { await deleteMutation.mutateAsync(id); } catch (err) { console.error('Failed:', err); }
  };

  const filteredTestimonials = testimonials.filter((t: Testimonial) => {
    if (filter === 'approved') return t.approved;
    if (filter === 'pending') return !t.approved;
    if (filter === 'featured') return t.featured;
    return true;
  });

  const stats = {
    total: testimonials.length,
    approved: testimonials.filter((t: Testimonial) => t.approved).length,
    pending: testimonials.filter((t: Testimonial) => !t.approved).length,
    featured: testimonials.filter((t: Testimonial) => t.featured).length,
  };

  if (isLoading) return <AdminLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Testimonials</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">Manage user testimonials</p>
          </div>
          <button 
            onClick={() => refetch()} 
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium min-h-[44px] w-full sm:w-auto"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            Error: {error instanceof Error ? error.message : 'Unknown'}
          </div>
        )}

        {/* Stats - 2x2 grid on mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {(['total', 'approved', 'pending', 'featured'] as const).map(k => (
            <div key={k} className="bg-white p-3 sm:p-4 rounded-lg border">
              <p className="text-xs sm:text-sm text-gray-500 capitalize">{k}</p>
              <p className="text-lg sm:text-xl font-semibold">{stats[k]}</p>
            </div>
          ))}
        </div>

        {/* Filter buttons - horizontal scroll on mobile */}
        <div className="bg-white p-3 sm:p-4 rounded-lg border overflow-x-auto mobile-scroll">
          <div className="flex gap-2 min-w-max">
            {(['all', 'approved', 'pending', 'featured'] as FilterType[]).map(f => (
              <button 
                key={f} 
                onClick={() => setFilter(f)} 
                className={`px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium min-h-[44px] whitespace-nowrap ${
                  filter === f ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? stats.total : stats[f]})
              </button>
            ))}
          </div>
        </div>

        {/* Testimonials list */}
        <div className="space-y-3 sm:space-y-4">
          {filteredTestimonials.map((t: Testimonial) => (
            <div key={t.id} className="bg-white rounded-lg border p-4 sm:p-6">
              {/* Mobile: Stacked layout */}
              <div className="flex items-start gap-3 sm:gap-4">
                <ProfileImage src={t.user_avatar} alt={t.user_name} name={t.user_name} size="sm" className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{t.user_name}</h3>
                  <p className="text-gray-700 my-2 text-sm">{t.content}</p>
                  <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
                    <span className="text-gray-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {t.approved ? 'Approved' : 'Pending'}
                    </span>
                    {t.featured && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Featured</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action buttons - horizontal on mobile */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                <button 
                  onClick={() => handleApprove(t.id, !t.approved)} 
                  disabled={approveMutation.isPending} 
                  className={`flex-1 sm:flex-none px-3 py-2 text-sm rounded-lg font-medium disabled:opacity-50 min-h-[44px] ${
                    t.approved ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {t.approved ? 'Unapprove' : 'Approve'}
                </button>
                <button 
                  onClick={() => handleFeature(t.id, !t.featured)} 
                  disabled={featureMutation.isPending} 
                  className={`flex-1 sm:flex-none px-3 py-2 text-sm rounded-lg font-medium disabled:opacity-50 min-h-[44px] ${
                    t.featured ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {t.featured ? 'Unfeature' : 'Feature'}
                </button>
                <button 
                  onClick={() => handleDelete(t.id)} 
                  disabled={deleteMutation.isPending} 
                  className="flex-1 sm:flex-none px-3 py-2 text-sm bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 min-h-[44px]"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          {filteredTestimonials.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No testimonials found</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
